import { completeWorldpayAccessWithout3ds } from "../../app-utils/worldpayAccess";
import Api from "../../Service/api";
import { T } from "../../app-utils/translator";
import { appMessage as message } from "../../app-utils/antdFeedback";
import { useContext, useEffect, useRef, useState } from "react";
import { AutoShipContext, LoadingContext, ProfileContext } from "../../App";
import { Button, Col, Form, Modal, Row, Spin, Input } from "antd";
import { LeftOutlined, RightCircleOutlined } from '@ant-design/icons';

const api = new Api();

const WorldPayAccess = ({ connector, refreshPaymentList }) => {
    const { setLoading } = useContext(LoadingContext);
    const { profileData } = useContext(ProfileContext);
    const { autoShipDetails } = useContext(AutoShipContext);
    const [form] = Form.useForm();

    const [worldpayAccessCheckout, setWorldpayAccessCheckout] = useState();
    const [worldpayAccessLoading, setWorldpayAccessLoading] = useState(false);
    const [worldpayAccessReady, setWorldpayAccessReady] = useState(false);
    const [, setWorldpayAccessDdcData] = useState();
    const [worldpayAccessChallengeModalOpen, setWorldpayAccessChallengeModalOpen] = useState(false);
    const [worldpayAccessFieldErrors, setWorldpayAccessFieldErrors] = useState({
        pan: false,
        expiry: false,
        cvv: false
    });
    const currentReferenceRef = useRef('');

    useEffect(() => {
        const listView = document.getElementsByClassName('listViewDiv')[0];
        listView?.classList?.add('adjustHeight');

        return () => {
            listView?.classList?.remove('adjustHeight');
        };
    }, []);

    const getWorldpayAccessCheckoutIdentity = () => {
        return process.env.REACT_APP_WORLDPAY_ACCESS_CHECKOUT_ID || '';
    };

    const submitWorldpayAccessCollectionForm = (collection) => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return Promise.reject(new Error('DOCUMENT_NOT_AVAILABLE'));
        }

        return new Promise((resolve, reject) => {
            const mountTarget = document.body || document.documentElement;
            if (!mountTarget) {
                reject(new Error('DOCUMENT_BODY_NOT_AVAILABLE'));
                return;
            }

            const iframeName = `autoship-worldpay-access-${Math.random().toString(36).slice(2)}`;
            const iframe = document.createElement('iframe');
            iframe.name = iframeName;
            iframe.id = iframeName;
            iframe.style.display = 'none';

            const cleanup = () => {
                window.removeEventListener('message', messageHandler);
                iframe.remove();
            };

            const resolveWithReference = (gatewayReference) => {
                cleanup();
                resolve(gatewayReference);
            };

            const messageHandler = (event) => {
                const eventData = typeof event?.data === 'string'
                    ? (() => {
                        try {
                            return JSON.parse(event.data);
                        } catch (parseError) {
                            return null;
                        }
                    })()
                    : event?.data;

                if (event?.origin !== collection.origin) {
                    return;
                }

                const gatewayReference = eventData?.SessionId || eventData?.sessionId || eventData?.message?.SessionId;
                if (gatewayReference) {
                    resolveWithReference(gatewayReference);
                }
            };

            mountTarget.appendChild(iframe);
            window.addEventListener('message', messageHandler);

            iframe.srcdoc = `
                <!DOCTYPE html>
                <html>
                <head>
                  <title>3DS Device Data Collection</title>
                </head>
                <body>
                  <form
                    id="collectionForm"
                    name="devicedata"
                    method="POST"
                    action="${collection.url}">
                    <input type="hidden" name="Bin" value="${collection.gatewayKey}">
                    <input type="hidden" name="JWT" value="${collection.gatewayToken}">
                  </form>
                  <script>
                    window.onload = function () {
                      document.getElementById('collectionForm').submit();
                    };
                  </script>
                </body>
                </html>
            `;
        });
    };

    const submitWorldpayAccessChallengeForm = async (challengeUrl, gatewayToken, targetId = '3d-secure-verification') => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            throw new Error('DOCUMENT_NOT_AVAILABLE');
        }

        const challengeOrigin = new URL(challengeUrl).origin;

        return new Promise((resolve, reject) => {
            let retryCount = 0;
            const maxRetries = 20;

            const cleanup = () => {
                window.removeEventListener('message', messageHandler);
            };

            const messageHandler = (event) => {
                if (event?.origin !== challengeOrigin) {
                    return;
                }

                const eventData = typeof event?.data === 'string'
                    ? (() => {
                        try {
                            return JSON.parse(event.data);
                        } catch (parseError) {
                            return null;
                        }
                    })()
                    : event?.data;

                const sessionId = eventData?.SessionId || eventData?.sessionId;
                const isCompleted = eventData?.MessageType === 'profile.completed' || eventData?.messageType === 'profile.completed';
                if (isCompleted && sessionId) {
                    cleanup();
                    resolve(sessionId);
                }
            };

            window.addEventListener('message', messageHandler);

            const mountChallenge = () => {
                const challengeFrame = document.getElementById(targetId);
                if (!challengeFrame || challengeFrame.tagName !== 'IFRAME') {
                    if (retryCount < maxRetries) {
                        retryCount += 1;
                        window.setTimeout(mountChallenge, 100);
                        return;
                    }
                    cleanup();
                    reject(new Error('WORLD_PAY_CHALLENGE_CONTAINER_NOT_FOUND'));
                    return;
                }

                challengeFrame.srcdoc = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>3DS Challenge</title>
                      <style>
                        html, body {
                          margin: 0;
                          width: 100%;
                          height: 100%;
                          overflow: hidden;
                          background: #fff;
                        }
                      </style>
                    </head>
                    <body>
                      <form
                        id="challengeForm"
                        method="POST"
                        action="${challengeUrl}">
                        <input type="hidden" name="JWT" value="${gatewayToken}">
                      </form>
                      <script>
                        window.onload = function () {
                          document.getElementById('challengeForm').submit();
                        };
                      </script>
                    </body>
                    </html>
                `;
            };

            window.setTimeout(mountChallenge, 0);
        });
    };

    useEffect(() => {
        const formElement = document.getElementById('card-form');
        const panContainer = document.getElementById('card-pan');
        const expiryContainer = document.getElementById('card-expiry');
        const cvvContainer = document.getElementById('card-cvv');
        if (!connector?.gatewayMidUuid || !formElement || !panContainer || !expiryContainer || !cvvContainer) {
            setWorldpayAccessCheckout(null);
            setWorldpayAccessReady(false);
            setWorldpayAccessFieldErrors({
                pan: false,
                expiry: false,
                cvv: false
            });
            return undefined;
        }

        let cancelled = false;
        let cleanupListeners = () => { };
        let initRetryCount = 0;
        const maxInitRetries = 20;

        const initWorldpayAccess = async () => {
            const currentForm = document.getElementById('card-form');
            const currentPan = document.getElementById('card-pan');
            const currentExpiry = document.getElementById('card-expiry');
            const currentCvv = document.getElementById('card-cvv');

            if (!currentForm || !currentPan || !currentExpiry || !currentCvv) {
                if (!cancelled && initRetryCount < maxInitRetries) {
                    initRetryCount += 1;
                    window.setTimeout(initWorldpayAccess, 100);
                }
                return;
            }

            if (typeof window.initWorldpayAccessCheckout !== 'function') {
                message.error('WP Access checkout is not available.');
                return;
            }

            const checkoutIdentity = getWorldpayAccessCheckoutIdentity();
            const connectorIdentity = connector?.gatewayMerchantId;
            if (!connectorIdentity && !checkoutIdentity) {
                setWorldpayAccessLoading(false);
                message.error('WP Access checkout identity is missing.');
                return;
            }

            try {
                setLoading(true);
                setWorldpayAccessLoading(true);
                setWorldpayAccessReady(false);
                setWorldpayAccessFieldErrors({
                    pan: false,
                    expiry: false,
                    cvv: false
                });

                const checkout = await window.initWorldpayAccessCheckout({
                    id: connectorIdentity || checkoutIdentity
                });

                if (cancelled) {
                    return;
                }

                setWorldpayAccessCheckout(checkout);
                setWorldpayAccessReady(true);

                const handleFieldChange = (event) => {
                    const fieldName = event?.detail?.field?.name;
                    const isValid = event?.detail?.['is-valid'];
                    if (!fieldName || !['pan', 'expiry', 'cvv'].includes(fieldName)) {
                        return;
                    }

                    setWorldpayAccessFieldErrors((prev) => ({
                        ...prev,
                        [fieldName]: isValid === false
                    }));
                };

                const handleFormReady = () => {
                    setWorldpayAccessReady(true);
                };

                currentForm.addEventListener('wp:field:change', handleFieldChange);
                currentForm.addEventListener('wp:form:ready', handleFormReady);

                cleanupListeners = () => {
                    currentForm.removeEventListener('wp:field:change', handleFieldChange);
                    currentForm.removeEventListener('wp:form:ready', handleFormReady);
                };
            } catch (error) {
                if (!cancelled) {
                    message.error(error?.message || T('failedAddCard'));
                }
            } finally {
                if (!cancelled) {
                    setWorldpayAccessLoading(false);
                    setLoading(false);
                }
            }
        };

        const timer = window.setTimeout(initWorldpayAccess, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            cleanupListeners();
        };
        // eslint-disable-next-line
    }, [connector?.gatewayMidUuid, connector?.gatewayMerchantId]);

    const pollTransactionStatus = async (reference) => {
        const ref = reference || currentReferenceRef.current;
        for (let i = 0; i < 3; i++) {
            const result = await api.mainRestCallService(`external/fetch-status?reference=${ref}`, 'GET');
            const status = result?.data?.status;

            if (status === 99) {
                setWorldpayAccessLoading(false);
                message.error(T('cardAddFailText'));
                return;
            }

            if (i < 2) {
                await new Promise((resolve) => setTimeout(resolve, 500));
            }
        }

        setWorldpayAccessLoading(false);
        refreshPaymentList?.();
        message.success(T('cardAddSuccess'));
    };

    const handleWorldpayAccessSubmit = async (event) => {
        event.preventDefault();

        if (!worldpayAccessCheckout) {
            message.error('WP Access checkout is still loading.');
            return;
        }

        const hasValidationError = Object.values(worldpayAccessFieldErrors).some(Boolean);
        if (hasValidationError) {
            message.error('Please enter valid card details.');
            return;
        }

        try {
            setWorldpayAccessLoading(true);

            const sessionState = await window.generateWorldpayAccessSessionState();
            const requestBody = {
                midUuid: connector?.gatewayMidUuid,
                issuerCode: profileData?.issuerCode,
                currency: autoShipDetails?.currency,
                clientName: form.getFieldValue('clientName') || profileData?.first_name || profileData?.name || '',
                source: 4,
                sessionReference: sessionState
            };

            const result = await api.mainRestCallService('pgapi/initiate-payment-instrument', 'POST', requestBody);
            currentReferenceRef.current = result?.data?.reference || '';
            if (result?.data?.url && result?.data?.gatewayKey && result?.data?.gatewayToken) {
                const ddcData = {
                    url: result.data.url,
                    gatewayKey: result.data.gatewayKey,
                    gatewayToken: result.data.gatewayToken,
                    reference: result.data.reference,
                    origin: new URL(result.data.url).origin
                };
                setWorldpayAccessDdcData(ddcData);
                currentReferenceRef.current = ddcData.reference;

                const gatewayReference = await submitWorldpayAccessCollectionForm(ddcData);
                const sessionId = String(gatewayReference || '').trim();
                if (!sessionId) {
                    throw new Error('WP Access session id is missing.');
                }

                const finalResult = await api.mainRestCallService('pgapi/initiate-pi-payment', 'POST', {
                    paymentId: sessionId,
                    reference: ddcData.reference
                });

                if (finalResult?.data?.reference) {
                    currentReferenceRef.current = finalResult.data.reference;
                }

                if (completeWorldpayAccessWithout3ds(finalResult, T('cardAddFailText'))) {
                    await pollTransactionStatus(currentReferenceRef.current);
                    return;
                }

                if (finalResult?.status === 1 && finalResult?.data) {
                    setWorldpayAccessChallengeModalOpen(true);
                    setWorldpayAccessLoading(false);
                    window.setTimeout(() => {
                        submitWorldpayAccessChallengeForm(
                            finalResult.data.url,
                            finalResult.data.gatewayToken,
                            '3d-secure-verification'
                        );
                    }, 0);
                } else {
                    throw new Error(T('cardAddFailText'));
                }
            } else if (result?.status === 1 && !result?.data?.url && !result?.data?.gatewayKey && !result?.data?.gatewayToken) {
                await pollTransactionStatus(result?.data?.reference || currentReferenceRef.current);
            } else {
                throw new Error(T('cardAddFailText'));
            }
        } catch (error) {
            const errorMessage = error?.message || T('cardAddFailText');
            message.error(errorMessage);
            setWorldpayAccessLoading(false);
        }
    };

    const addCardStatus = async (event) => {
        const eventData = event?.data;
        const status = typeof eventData === 'string'
            ? (eventData === 'card_status_check' ? 'SUCCESS' : '')
            : (eventData?.status || '');

        if (eventData === 'card_status_check' || eventData?.type === 'card_status_check') {
            setWorldpayAccessChallengeModalOpen(false);

            if (String(status).toUpperCase() === 'FAILED') {
                setWorldpayAccessLoading(false);
                refreshPaymentList?.();
                message.error(T('cardAddFailText'));
                return;
            }

            setWorldpayAccessLoading(true);
            await pollTransactionStatus(currentReferenceRef.current);
        }
    };

    useEffect(() => {
        window.addEventListener("message", addCardStatus);
        return () => window.removeEventListener("message", addCardStatus);
        // eslint-disable-next-line
    }, []);

    return (
        <div className="mt-4">
            <div id="worldpay-access-html" style={{ width: '100%' }}>
                <Form id="card-form" form={form} layout="vertical" style={{ width: '100%', marginTop: 12 }} onSubmitCapture={handleWorldpayAccessSubmit}>
                    <Col span={24} style={{ marginBottom: 20 }}>
                        <label><span className="requiredField">*</span> {T('card_number')}</label>
                        <div id="card-pan" className="worldpay-access-field worldpay-access-host" />
                        {worldpayAccessFieldErrors.pan ? <p className="worldpay-access-error">{T('InvalidCardNumber')}</p> : null}
                    </Col>

                    <Col span={24} style={{ marginBottom: 20 }}>
                        <Form.Item name="clientName" label={<><span className="requiredField">*</span> {T('cardName')}</>} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                            rules={[
                                { required: true, message: T('requiredError') },
                                { pattern: /^[a-zA-Z\s-]{2,30}$/, message: T('Please enter a valid cardholder name') },
                            ]}>
                            <Input key="ach-accountnumber" placeholder="Card holder's name" maxLength={30} autoComplete="off" />
                        </Form.Item>
                    </Col>

                    <Col span={24} style={{ marginBottom: 20 }}>
                        <Row gutter={12}>
                            <Col span={12}>
                                <label><span className="requiredField">*</span> {T('expiry_date')}</label>
                                <div id="card-expiry" className="worldpay-access-field worldpay-access-host" />
                                {worldpayAccessFieldErrors.expiry ? <p className="worldpay-access-error">{T('InvalidExpirationDate')}</p> : null}
                            </Col>
                            <Col span={12}>
                                <label><span className="requiredField">*</span> {T('cvv')}</label>
                                <div id="card-cvv" className="worldpay-access-field worldpay-access-host" />
                                {worldpayAccessFieldErrors.cvv ? <p className="worldpay-access-error">{T('InvalidCVV')}</p> : null}
                            </Col>
                        </Row>
                    </Col>

                    <Col style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }} span={24}>
                        <Button
                            className="btn back-btn"
                            style={{ marginRight: 12, flex: 1 }}
                            onClick={() => refreshPaymentList?.()}
                        >
                            <LeftOutlined /> {T('back')} <span></span>
                        </Button>
                        <Button
                            className="btn newCardbutton"
                            htmlType="submit"
                            loading={worldpayAccessLoading}
                            disabled={!worldpayAccessReady || worldpayAccessLoading}
                            style={{ flex: 1, width: 'auto' }}
                        >
                            <LeftOutlined style={{ color: 'transparent' }} /> {T('submit_text')} <RightCircleOutlined />
                        </Button>
                    </Col>
                </Form>
            </div>

            {!worldpayAccessReady ? (
                <div className="worldpay-access-loading">
                    <Spin tip="Loading WP Access..." size="large" />
                </div>
            ) : null}

            <Modal
                className="worldpay-access-modal"
                open={worldpayAccessChallengeModalOpen}
                closable={false}
                centered
                width={420}
                footer={null}
                destroyOnClose
            >
                <div style={{ width: '100%', minHeight: 500 }}>
                    <iframe
                        id="3d-secure-verification"
                        name="3d-secure-verification"
                        style={{ width: '100%', height: '600px', border: 'none' }}
                        title="3D Secure Verification"
                    />
                </div>
            </Modal>
        </div>
    );
};

export default WorldPayAccess;
